import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import { CoordinatorModeProvider } from './context/CoordinatorModeContext'

// Eagerly imported: Home is the warm-start surface for every signed-in
// user — splitting it adds a flash on the most-trafficked path.
import Home from './pages/Home'

// Everything else is route-split. The build previously shipped one
// ~557 kB-gzipped main.js containing every page (Dashboard's ~3.7k LOC
// + recharts, SigninSheet's react-quill-new, the signature canvas,
// ClusterMap's leaflet glue, etc.) regardless of where the user
// actually went. React.lazy + Suspense pushes those into their own
// chunks fetched on demand.
const Login              = lazy(() => import('./pages/Login'))
const Participants       = lazy(() => import('./pages/Participants'))
const GlobalSearch       = lazy(() => import('./pages/GlobalSearch'))
const ParticipantProfile = lazy(() => import('./pages/ParticipantProfile'))
const ActivityDetail     = lazy(() => import('./pages/ActivityDetail'))
const SessionDetail      = lazy(() => import('./pages/SessionDetail'))
const RecurringActivities = lazy(() => import('./pages/RecurringActivities'))
const LogSession         = lazy(() => import('./pages/LogSession'))
const Campaigns          = lazy(() => import('./pages/Campaigns'))
const CampaignDetail     = lazy(() => import('./pages/CampaignDetail'))
const More               = lazy(() => import('./pages/More'))
const ClusterMap         = lazy(() => import('./pages/ClusterMap'))
const NucleusDetail      = lazy(() => import('./pages/NucleusDetail'))
const Celebrations       = lazy(() => import('./pages/Celebrations'))
const Households         = lazy(() => import('./pages/Households'))
const HouseholdDetail    = lazy(() => import('./pages/HouseholdDetail'))
const Account            = lazy(() => import('./pages/Account'))
const Dashboard          = lazy(() => import('./pages/Dashboard'))
const RegistrationForm   = lazy(() => import('./pages/RegistrationForm'))
const Reimbursements     = lazy(() => import('./pages/Reimbursements'))
const TreasurerReport    = lazy(() => import('./pages/TreasurerReport'))
const SigninSheet        = lazy(() => import('./pages/SigninSheet'))
const MinorRelease       = lazy(() => import('./pages/MinorRelease'))
const MissingReleases    = lazy(() => import('./pages/MissingReleases'))
const MinorReleasesList  = lazy(() => import('./pages/MinorReleasesList'))
const DoorCheckin        = lazy(() => import('./pages/DoorCheckin'))
const ResetPassword      = lazy(() => import('./pages/ResetPassword'))
const Unsubscribe        = lazy(() => import('./pages/Unsubscribe'))

// Shared loading state — matches the auth-loading screen so a chunk
// fetch and an auth resolve look the same to the user.
const PageLoader = () => (
  <div className="min-h-screen bg-amber-50 flex items-center justify-center">
    <div className="text-amber-700 text-lg">Loading...</div>
  </div>
)

const ProtectedRoute = ({ children, hasProfile }) => {
  const { user, loading } = useAuth()
  if (loading) return <PageLoader />
  if (!user) return <Navigate to="/login" />
  return children
}

function AppRoutes() {
  const { user, profile, loading, recoveryActive } = useAuth()
  const hasProfile = !!profile

  if (loading) return <PageLoader />

  // Password-recovery short-circuit. When Supabase fires PASSWORD_RECOVERY
  // (user clicked a reset link), AuthContext flips recoveryActive=true.
  // The recovery session also looks "signed in," so without this gate
  // the router would happily send the user to / and they'd never see
  // the reset form. ResetPassword.js calls clearRecovery() on success.
  if (recoveryActive && window.location.pathname !== '/reset-password') {
    return <Navigate to="/reset-password" replace />
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/" /> : <Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/" element={<ProtectedRoute hasProfile={hasProfile}><Home /></ProtectedRoute>} />
        <Route path="/participants" element={<ProtectedRoute hasProfile={hasProfile}><Participants /></ProtectedRoute>} />
        <Route path="/search" element={<ProtectedRoute hasProfile={hasProfile}><GlobalSearch /></ProtectedRoute>} />
        <Route path="/participants/:participantId" element={<ProtectedRoute hasProfile={hasProfile}><ParticipantProfile /></ProtectedRoute>} />
        <Route path="/activity/:activityId" element={<ProtectedRoute hasProfile={hasProfile}><ActivityDetail /></ProtectedRoute>} />
        <Route path="/activity/:activityId/signin" element={<ProtectedRoute hasProfile={hasProfile}><SigninSheet /></ProtectedRoute>} />
        <Route path="/session/:sessionId" element={<ProtectedRoute hasProfile={hasProfile}><SessionDetail /></ProtectedRoute>} />
        <Route path="/activities/:type" element={<ProtectedRoute hasProfile={hasProfile}><RecurringActivities /></ProtectedRoute>} />
        <Route path="/activities" element={<ProtectedRoute hasProfile={hasProfile}><RecurringActivities /></ProtectedRoute>} />
        <Route path="/log/:activityId" element={<ProtectedRoute hasProfile={hasProfile}><LogSession /></ProtectedRoute>} />
        <Route path="/campaigns" element={<ProtectedRoute hasProfile={hasProfile}><Campaigns /></ProtectedRoute>} />
        <Route path="/campaigns/:campaignId" element={<ProtectedRoute hasProfile={hasProfile}><CampaignDetail /></ProtectedRoute>} />
        <Route path="/more" element={<ProtectedRoute hasProfile={hasProfile}><More /></ProtectedRoute>} />
        <Route path="/map" element={<ProtectedRoute hasProfile={hasProfile}><ClusterMap /></ProtectedRoute>} />
        <Route path="/nucleus/:nucleusId" element={<ProtectedRoute hasProfile={hasProfile}><NucleusDetail /></ProtectedRoute>} />
        <Route path="/celebrations" element={<ProtectedRoute hasProfile={hasProfile}><Celebrations /></ProtectedRoute>} />
        <Route path="/households" element={<ProtectedRoute hasProfile={hasProfile}><Households /></ProtectedRoute>} />
        <Route path="/households/:id" element={<ProtectedRoute hasProfile={hasProfile}><HouseholdDetail /></ProtectedRoute>} />
        <Route path="/account" element={<ProtectedRoute hasProfile={hasProfile}><Account /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute hasProfile={hasProfile}><Dashboard /></ProtectedRoute>} />
        <Route path="/register/:formId" element={<RegistrationForm />} />
        <Route path="/release" element={<MinorRelease />} />
        <Route path="/missing-releases" element={<ProtectedRoute hasProfile={hasProfile}><MissingReleases /></ProtectedRoute>} />
        <Route path="/minor-releases" element={<ProtectedRoute hasProfile={hasProfile}><MinorReleasesList /></ProtectedRoute>} />
        <Route path="/activity/:activityId/checkin" element={<ProtectedRoute hasProfile={hasProfile}><DoorCheckin /></ProtectedRoute>} />
        <Route path="/reimbursements" element={<ProtectedRoute hasProfile={hasProfile}><Reimbursements /></ProtectedRoute>} />
        <Route path="/treasurer-report" element={<ProtectedRoute hasProfile={hasProfile}><TreasurerReport /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Suspense>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <CoordinatorModeProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </CoordinatorModeProvider>
    </AuthProvider>
  )
}
